// Localização: frontend/src/pages/Financeiro/ContasReceberPage.jsx
// Página de Contas a Receber

import React, { useState, useEffect, useRef } from 'react';
import PrimaryButton from '../../components/Common/PrimaryButton';
import FormContaReceber from '../../components/Financeiro/FormContaReceber';
import ListaContasReceber from '../../components/Financeiro/ListaContasReceber';
import ModalRecebimento from '../../components/Financeiro/ModalRecebimento';
import ConfirmacaoModal from '../../components/Common/ConfirmacaoModal';
import AcessoRapidoFinanceiro from '../../components/Financeiro/AcessoRapidoFinanceiro';
import FloatingCalendarFilter from '../../components/Financeiro/FloatingCalendarFilter';
import CollapsibleStatusFilter from '../../components/Financeiro/CollapsibleStatusFilter';
import { useContasReceber } from '../../hooks/useFinanceiro';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { Plus, Calendar, Tag } from 'lucide-react';

const ContasReceberPage = () => {
    const { isAuthenticated } = useAuth();
    const { checkPermission } = usePermissions();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isRecebimentoOpen, setIsRecebimentoOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [contaSelecionada, setContaSelecionada] = useState(null);
    const [contaParaDeletar, setContaParaDeletar] = useState(null);
    const [paginaAtual, setPaginaAtual] = useState(1);
    // Inicializar com "próximos 7 dias"
    const [periodoFluxo, setPeriodoFluxo] = useState('custom');
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodFilterRef = useRef(null);
    const periodButtonRef = useRef(null);
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusFilterRef = useRef(null);
    const statusButtonRef = useRef(null);
    const [filters, setFilters] = useState(() => {
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(today.getDate() + 7);
        return {
            startDate: today.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            status: ['Pendente', 'Vencido'],
            search: ''
        };
    });

    const {
        contas,
        pagination,
        loading,
        error,
        fetchContas,
        createConta,
        updateConta,
        deleteConta,
        receber,
        receberParcial
    } = useContasReceber();

    // Permissões - se não encontrar, assume true (permissão concedida por padrão)
    const canCreate = checkPermission('financeiro', 'can_create') !== false;
    const canEdit = checkPermission('financeiro', 'can_edit') !== false;
    const canDelete = checkPermission('financeiro', 'can_delete') !== false;
    const canAccess = checkPermission('financeiro', 'can_access') !== false;

    // Carregar contas ao montar e quando página mudar
    useEffect(() => {
        carregarContas();
    }, [paginaAtual, filters]);

    const carregarContas = async () => {
        const params = {
            page: paginaAtual,
            limit: 20
        };

        // Só adicionar filtros se tiverem valor
        if (filters.startDate) {
            params.data_inicio = filters.startDate;
        }
        if (filters.endDate) {
            params.data_fim = filters.endDate;
        }
        if (filters.status && filters.status.length > 0) {
            params.status = filters.status.join(',');
        }
        if (filters.search && filters.search.trim()) {
            params.search = filters.search.trim();
        }

        await fetchContas(params);
    };

    const handleDateRangeChange = (start, end) => {
        setFilters(prev => ({
            ...prev,
            startDate: start,
            endDate: end
        }));
        setPaginaAtual(1);
    };

    const handlePeriodoChange = (periodo) => {
        setPeriodoFluxo(periodo);
        
        // Se for 'custom', não recalcular datas (elas já foram definidas pelo onDateRangeChange)
        if (periodo === 'custom') {
            setPaginaAtual(1);
            return;
        }

        const today = new Date();
        const start = new Date();
        const end = new Date();

        if (periodo === 'dia') {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
        } else if (periodo === 'semana') {
            const dayOfWeek = today.getDay();
            start.setDate(today.getDate() - dayOfWeek);
            end.setDate(start.getDate() + 6);
        } else if (periodo === 'mes') {
            start.setDate(1);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
        }

        setFilters(prev => ({
            ...prev,
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        }));
        setPaginaAtual(1);
    };

    const handleOpenForm = (conta = null) => {
        setContaSelecionada(conta);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setContaSelecionada(null);
    };

    const handleSave = async (data) => {
        try {
            if (contaSelecionada) {
                await updateConta(contaSelecionada.id, data);
            } else {
                await createConta(data);
                
                // Se for conta recorrente, aguardar um pouco antes de recarregar
                // para dar tempo do backend processar todas as contas geradas
                if (data.tipo_lancamento === 'recorrente_fixo' || 
                    data.tipo_lancamento === 'recorrente_variavel') {
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Aguardar 1 segundo
                }
            }
            
            // Recarregar contas após um pequeno delay para garantir que o backend terminou
            setTimeout(async () => {
                await carregarContas();
            }, 500);
            
            handleCloseForm();
        } catch (error) {
            console.error('Erro ao salvar conta:', error);
        }
    };

    const handleOpenRecebimento = (conta) => {
        setContaSelecionada(conta);
        setIsRecebimentoOpen(true);
    };

    const handleCloseRecebimento = () => {
        setIsRecebimentoOpen(false);
        setContaSelecionada(null);
    };

    const handleReceber = async (contaId, tipo, data) => {
        try {
            if (tipo === 'total') {
                await receber(contaId, data);
            } else {
                await receberParcial(contaId, data);
            }
            await carregarContas();
            handleCloseRecebimento();
        } catch (error) {
            console.error('Erro ao registrar recebimento:', error);
            // Não re-lançar o erro, pois o hook já mostra toast de erro
        }
    };

    const handleOpenDelete = (contaId) => {
        setContaParaDeletar(contaId);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDelete = () => {
        setIsDeleteModalOpen(false);
        setContaParaDeletar(null);
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteConta(contaParaDeletar);
            await carregarContas();
            handleCloseDelete();
        } catch (error) {
            console.error('Erro ao deletar conta:', error);
        }
    };

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown de Status ao clicar fora
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleClickOutside = (event) => {
            if (
                statusFilterRef.current &&
                statusButtonRef.current &&
                !statusFilterRef.current.contains(event.target) &&
                !statusButtonRef.current.contains(event.target)
            ) {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter]);

    // Fechar dropdown de Status ao pressionar Escape
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showStatusFilter]);

    if (!canAccess) {
        return (
            <div className="p-6">
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800">
                        Você não tem permissão para acessar esta funcionalidade.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full">
            {/* Acesso Rápido às Funções - Posição Fixa - Centralizado - Abaixo do Header */}
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-20 lg:top-20">
                <AcessoRapidoFinanceiro />
            </div>

            {/* Botões Flutuantes à Direita - Período e Status */}
            <div className="fixed top-20 right-2 sm:right-4 sm:right-6 lg:right-8 z-30 flex flex-col gap-2">
                {/* Botão de Período */}
                <div ref={periodButtonRef}>
                    <button
                        onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                        className={`
                            flex items-center justify-center gap-2
                            px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                            transition-all duration-200 font-medium 
                            text-xs sm:text-sm
                            border shadow-lg hover:shadow-xl
                            backdrop-blur-sm
                            ${showPeriodFilter 
                                ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
                        `}
                        title={showPeriodFilter ? 'Ocultar filtro de período' : 'Mostrar filtro de período'}
                    >
                        <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showPeriodFilter ? 'scale-110' : ''} transition-transform`} />
                        <span className="hidden sm:inline whitespace-nowrap">Período</span>
                    </button>
                </div>

                {/* Botão de Status */}
                <div ref={statusButtonRef}>
                    <button
                        onClick={() => setShowStatusFilter(!showStatusFilter)}
                        className={`
                            flex items-center justify-center gap-2
                            px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                            transition-all duration-200 font-medium 
                            text-xs sm:text-sm
                            border shadow-lg hover:shadow-xl
                            backdrop-blur-sm
                            ${showStatusFilter
                                ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }
                        `}
                        title={showStatusFilter ? 'Ocultar filtro de status' : 'Mostrar filtro de status'}
                    >
                        <Tag className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showStatusFilter ? 'scale-110' : ''} transition-transform`} />
                        <span className="hidden sm:inline whitespace-nowrap">Status</span>
                    </button>
                </div>
            </div>

            {/* Filtro de Calendário Flutuante - Dropdown */}
            {showPeriodFilter && (
                <div ref={periodFilterRef} className="fixed top-20 left-2 right-2 sm:left-auto sm:right-4 sm:top-36 lg:right-8 z-[100] w-auto sm:w-auto sm:max-w-md max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <FloatingCalendarFilter
                        periodo={periodoFluxo}
                        onPeriodoChange={handlePeriodoChange}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        onDateRangeChange={handleDateRangeChange}
                        noNegativeMargin={true}
                        customZIndex="z-[100]"
                        showOnlyContent={true}
                    />
                </div>
            )}

            {/* Filtro de Status Flutuante - Dropdown */}
            {showStatusFilter && (
                <div ref={statusFilterRef} className="fixed top-32 left-2 right-2 sm:left-auto sm:right-4 sm:top-48 lg:right-8 z-[100] w-auto sm:w-auto sm:min-w-[200px] max-h-[calc(100vh-8rem)] overflow-y-auto">
                    <CollapsibleStatusFilter
                        status={filters.status}
                        onStatusChange={(newStatus) => {
                            setFilters(prev => ({ ...prev, status: newStatus }));
                            setPaginaAtual(1);
                        }}
                        type="receber"
                        showOnlyContent={true}
                    />
                </div>
            )}

            {/* Conteúdo com espaçamento para o AcessoRapido fixo */}
            <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-10 space-y-6">

            <div className="flex items-center justify-end">
                {canCreate && (
                    <PrimaryButton onClick={() => handleOpenForm()}>
                        <Plus className="h-4 w-4 mr-2" />
                        Nova Conta a Receber
                    </PrimaryButton>
                )}
            </div>

            {/* Lista de Contas */}
            <ListaContasReceber
                contas={contas}
                isLoading={loading}
                onEdit={handleOpenForm}
                onDelete={handleOpenDelete}
                onReceber={handleOpenRecebimento}
                pagination={pagination}
                onPageChange={setPaginaAtual}
                canEdit={canEdit}
                canDelete={canDelete}
                receber={receber}
                receberParcial={receberParcial}
                onRefresh={carregarContas}
            />

            {/* Modais */}
            <FormContaReceber
                isOpen={isFormOpen}
                onClose={handleCloseForm}
                conta={contaSelecionada}
                onSave={handleSave}
            />

            <ModalRecebimento
                isOpen={isRecebimentoOpen}
                onClose={handleCloseRecebimento}
                conta={contaSelecionada}
                onReceber={handleReceber}
            />

            <ConfirmacaoModal
                isOpen={isDeleteModalOpen}
                onClose={handleCloseDelete}
                onConfirm={handleConfirmDelete}
                title="Cancelar Conta a Receber"
                message="Tem certeza que deseja cancelar esta conta a receber? Esta ação não pode ser desfeita."
                confirmText="Cancelar Conta"
                cancelText="Voltar"
            />
            </div>
        </div>
    );
};

export default ContasReceberPage;
